import { AxiosError } from 'axios';

type NavigateFunction = (
  to: string,
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  options?: { replace?: boolean; state?: any },
) => void;

export interface Context {
  token: string | null;
  isLoading: boolean;
  navigate?: NavigateFunction;
  error?: AxiosError;
  /** Which provider the in-flight or most recent OAuth attempt was for -- needed to
   * build a provider-specific error/success message, since the backend's response
   * doesn't carry it back. */
  provider?: OAuthProvider;
  /** Set from the success response's own `justLinked` field. Only ever true for an
   * OAuth login; password login never sets this. */
  justLinked?: boolean;
}

export enum State {
  INITIAL = 'idle',
  AUTHENTICATING = 'AUTHENTICATING',
  AUTHENTICATED = 'AUTHENTICATED',
  RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST',
  RESET_PASSWORD = 'RESET_PASSWORD',
  EMAIL_SENT = 'EMAIL_SENT',
  PASSWORD_RESETED = 'PASSWORD_RESETED',
  OAUTH_AUTHENTICATING = 'OAUTH_AUTHENTICATING',
  ERROR = 'error',
}

export enum EventType {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST',
  RESET_PASSWORD = 'RESET_PASSWORD',
  OAUTH_LOGIN = 'OAUTH_LOGIN',
}

export type OAuthProvider = 'google' | 'microsoft';

/**
 * avrio-backend's shared error response shape, plus the `code` field added
 * for OAuth sign-in (avrio-backend PR locking this contract, 2026-09-17):
 * `code` is additive and null on every error this app already handles
 * (bad password, expired reset token, etc.) -- only OAuth's two
 * copy-selectable cases set it.
 */
export interface BackendErrorBody {
  status: number;
  error: string;
  message: string;
  path: string;
  code: 'NO_ACCOUNT_MATCH' | 'EMAIL_NOT_VERIFIED' | null;
}

/** Success response for both password and OAuth login. `justLinked` is
 * OAuth-only; password login's response never carries it. */
export interface LoginResponse {
  token: string;
  expiresIn: number;
  justLinked?: boolean;
}

export type loginEvent = {
  type: EventType.LOGIN;
  payload: {
    email: string;
    password: string;
    navigate: NavigateFunction;
  };
};

export type oauthLoginEvent = {
  type: EventType.OAUTH_LOGIN;
  payload: {
    provider: OAuthProvider;
    navigate: NavigateFunction;
  };
};

export type logoutEvent = {
  type: EventType.LOGOUT;
};

export type resetPasswordRequestEvent = {
  type: EventType.RESET_PASSWORD_REQUEST;
  payload: {
    email: string;
  };
};

export type resetPasswordEvent = {
  type: EventType.RESET_PASSWORD;
  payload: {
    password: string;
    token: string;
    navigate: NavigateFunction;
  };
};

export type Event =
  | loginEvent
  | logoutEvent
  | resetPasswordRequestEvent
  | resetPasswordEvent
  | oauthLoginEvent;
